import { computed, watch } from 'vue'
import store from '@/store'
import { CallTriage } from '@/types/casts'
import { useCurrentCast } from '@/modules/casts/compositions'
import { useCurrentInvitedCaster } from '@/modules/casts/compositions'
import { useCurrentUserProfile } from '@/modules/user/compositions'
import { DeviceList } from '@/types/usermedia'
import { useAddLog } from '@/modules/logging/compositions'

const resetHasDetectedDeviceChanges = async () => {
  await store.dispatch.usermedia.setHasDetectedDeviceChanges(false)
}

const getDeviceListFingerprint = (devices: DeviceList|null): string => {
  if (!devices) return ''
  const audioIds = devices.audio.input.map((d) => d.deviceId).sort().join(',')
  const videoIds = devices.video.input.map((d) => d.deviceId).sort().join(',')
  return `${audioIds}|${videoIds}`
}

/**
 * Composable that watches for device changes and handles anonymous users
 * who are already accepted in a cast. When devices change, it moves them
 * back to selftest to verify hardware and be re-moderated.
 */
const useDeviceChangeDetection = (isWatching: boolean = false) => {
  const { currentCast } = useCurrentCast()
  const { currentTriage, isAnonymous } = useCurrentInvitedCaster()
  const { currentUserId } = useCurrentUserProfile()
  const { addLogNoWait } = useAddLog()

  const deviceList = computed(() => store.state.usermedia.deviceList)
  const hasDetectedDeviceChanges = computed(() => store.state.usermedia.hasDetectedDeviceChanges)

  // eslint-disable-next-line complexity
  const handleDeviceChange = async (newDevices: DeviceList|null, oldDevices?: DeviceList|null) => {
    if (!newDevices || !oldDevices) return
    if (!isAnonymous.value || !currentUserId.value || !currentCast.value) return
    const triages = [CallTriage.Queued, CallTriage.Unverified, CallTriage.InModeration, CallTriage.Accepted]
    if (!triages.includes(currentTriage.value)) return

    const currFingerprint = getDeviceListFingerprint(newDevices)
    const prevFingerprint = getDeviceListFingerprint(oldDevices)
    if (currFingerprint === prevFingerprint || !prevFingerprint) return

    try {
      await store.dispatch.usermedia.setHasDetectedDeviceChanges(true)
      await store.dispatch.usermedia.setIsSelfTestInSessionDone(false)

      await store.dispatch.casts.setCasterTriage({
        userId: currentUserId.value,
        triage: CallTriage.Selftest,
        castId: currentCast.value.id
      })

      addLogNoWait('Device change detected for anonymous user - moved back to selftest', 'info', {
        previousDevices: prevFingerprint,
        currentDevices: currFingerprint
      })
    } catch (error) {
      console.error('Error handling device change for anonymous user', error)
    }
  }

  if (isWatching) {
    watch(deviceList, handleDeviceChange, { deep: true })
  }

  return {
    deviceList,
    handleDeviceChange,
    hasDetectedDeviceChanges,
    resetHasDetectedDeviceChanges
  }
}

export default useDeviceChangeDetection
